<template>
  <div class="sectores-container">
    <div class="chart-index">
      <div class="chart-header">
        <div class="header-content">
          <h3 class="chart-title">Análisis por Sectores</h3>
          <button class="info-toggle" @click="toggleInfoSection" aria-label="Información sobre direccionalidad">
            <span class="material-icons">help_outline</span>
          </button>
        </div>
        
        <!-- Info Section -->
        <div v-if="showInfoSection" class="info-section">
          <div class="info-card">
            <div class="info-header">
              <span class="material-icons">info</span>
              <h4>¿Qué es la direccionalidad?</h4>
            </div>
            <div class="info-content">
              <p>La direccionalidad mide el porcentaje de valores que están en tendencia alcista en diferentes horizontes temporales:</p>
              <div class="indicators-explanation">
                <div class="indicator-item">
                  <div class="indicator-badge corto">Corto</div>
                  <span>Últimas 20 sesiones (~1 mes)</span>
                </div>
                <div class="indicator-item">
                  <div class="indicator-badge medio">Medio</div>
                  <span>Últimas 70 sesiones (~3 meses)</span>
                </div>
                <div class="indicator-item">
                  <div class="indicator-badge largo">Largo</div>
                  <span>Últimas 220 sesiones (~1 año)</span>
                </div>
              </div>
              <div class="explanation-note">
                <span class="material-icons">lightbulb</span>
                <span>Un valor alto indica que la mayoría de valores del sector están en tendencia alcista</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="chart-content">
        <div class="visualization-wrapper"> 
          <div class="treemap-column">
            <div class="treemap-wrapper">
              <highcharts :options="treeOptions" :callback="_initGraph"></highcharts>
            </div>
            <div class="controls-container">
              <div class="time-controls">
                <div class="time-buttons">
                  <button 
                    v-for="(button, index) in timeButtons" 
                    :key="index"
                    :id="button.id" 
                    :class="['time-btn', activeButton === button.id ? 'active' : '']"
                    @click="setActiveButton(button.id)">
                    <span class="btn-label">{{ button.label }}</span>
                    <span class="btn-subtitle">{{ button.subtitle }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="nested-bars-column">
            <div v-if="processedSectorData.length === 0" class="no-data-message">
              Cargando datos del sector o no hay datos que mostrar...
            </div>
            <div v-else class="nested-bars-list">
              <div v-for="broadSector in processedSectorData" :key="broadSector.id" class="broad-sector-group">
                <div class="broad-sector-header">
                  <div class="sector-name-row">
                    <h4 class="broad-sector-name">{{ getTranslatedSectorName(broadSector.name) }}</h4>
                    <span :class="['sector-profit', getProfitClass(broadSector.profit)]">{{ formatSectorProfit(broadSector.profit) }}</span>
                  </div>
                </div>
                <div v-if="broadSector.specificSectors && broadSector.specificSectors.length > 0" class="specific-sectors-list">
                  <div v-for="specificSector in broadSector.specificSectors" :key="specificSector.id" class="specific-sector-item">
                    <div class="specific-sector-name" :title="getTranslatedSectorName(specificSector.name)">
                      {{ getTranslatedSectorName(specificSector.name) }}
                    </div>
                  </div>
                </div>
                <div v-else class="no-specific-sectors">
                  No hay subsectores para {{getTranslatedSectorName(broadSector.name)}}.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="sectors-wrapper">
      <Sector
        :sector_timing="sector_timing_1m"
        :title="best_sector.one_month.title"
        :eurekers_values="sectors_data.eurekers_values"
        :five_sectors="sectors_data.best_five_sector_1m"/>
      <Sector 
        :sector_timing="sector_timing_3m"
        :title="best_sector.three_month.title"
        :eurekers_values="sectors_data.eurekers_values"
        :five_sectors="sectors_data.best_five_sector_3m"/>
      <Sector 
        :sector_timing="sector_timing_1y"
        :title="best_sector.one_year.title"
        :eurekers_values="sectors_data.eurekers_values"
        :five_sectors="sectors_data.best_five_sector_1y"/>
    </div>

    <TableSector 
      :eurekers_values="sectors_data.eurekers_values"
      :sectors_table_data="translatedTableData"
      :eurekers_values_ready="eurekers_values_ready"/>
  </div>
</template>


<script>
import Sector from '@/components/directionality/Sector.vue';
import TableSector from '@/components/directionality/TableSector.vue';

export default {
  name: 'Sectores',
  components: {
    Sector,
    TableSector,
  },

  props: {
    best_sector : Object,
    sectors_data: Object,
    eurekers_values_ready: Boolean,
  },
  data: () => ({
    activeButton: 'sector_1m',
    sector_timing_1m: '1m',
    sector_timing_3m: '3m',
    sector_timing_1y: '1y',
    timeButtons: [
      { id: 'sector_1m', label: 'corto', subtitle: '1 mes', class: 'btn-short' },
      { id: 'sector_3m', label: 'medio', subtitle: '3 meses', class: 'btn-middle' },
      { id: 'sector_1y', label: 'largo', subtitle: '1 año', class: 'btn-large' }
    ],
    treeOptions: {
      chart: { 
        type: "treemap",
        backgroundColor: 'var(--color-background)',
        borderRadius: 8,
        animation: true,
      },
      treemap: { 
        crisp: false,
        layoutAlgorithm: 'squarified',
        alternateStartingDirection: true,
        levelIsConstant: false,
      },
      series: [{
        levels: [{
          level: 1,
          layoutAlgorithm: 'squarified',
          borderWidth: 2,
          borderColor: 'var(--color-surface)',
          dataLabels: {
            enabled: true,
            align: 'center',
            verticalAlign: 'middle',
            useHTML: true,
            style: {
              fontSize: '15px',
              fontWeight: '500',
              color: 'white',
              textOutline: 'none',
              textShadow: 'none'
            },
            formatter: function() {
              // Calculate font size based on square area
              const point = this.point;
              const area = point.shapeArgs ? point.shapeArgs.width * point.shapeArgs.height : 0;
              
              // Define minimum and maximum font sizes
              const minFontSize = 12;
              const maxFontSize = 50;
              
              // Calculate font size based on square root of area (scales better with perception)
              // Adjust the divisor (15) to fine-tune the scaling
              let fontSize = Math.sqrt(area) / 8;
              fontSize = Math.max(minFontSize, Math.min(maxFontSize, fontSize));
              
              // Parse the name to separate sector name and profit
              const fullName = this.point.name;
              const lastSpaceIndex = fullName.lastIndexOf(' ');
              const possibleProfit = fullName.substring(lastSpaceIndex + 1);
              
              let sectorName = '';
              let profit = '';
              
              // Check if the last part looks like a percentage (contains % or is a number followed by %)
              if (possibleProfit.includes('%') || /^-?\d+\.?\d*%?$/.test(possibleProfit)) {
                sectorName = fullName.substring(0, lastSpaceIndex);
                profit = possibleProfit.includes('%') ? possibleProfit : possibleProfit + '%';
              } else {
                sectorName = fullName;
              }
              
              // Convert spaces to newlines in sector name
              const formattedSectorName = sectorName.replace(/\s+/g, '<br>');
              
              // Build the final display text
              let displayText = formattedSectorName;
              if (profit) {
                displayText += '<br>' + profit;
              }
              
              // Return HTML with inline style for font size
              return `<div style="font-size: ${fontSize}px; font-weight: 500; color: white; text-align: center; line-height: 1.1; display: flex; flex-direction: column; justify-content: center; height: 100%; font-family: 'Open Sans', sans-serif; text-shadow: 2px 2px 3px rgba(0, 0, 0, 0.5);">${displayText}</div>`;
            }
          }
        }],
        data: [],
        animation: true,
        states: {
          hover: {
            brightness: 0.1,
            borderColor: 'var(--color-highlight)',
            borderWidth: 3
          }
        }
      }],
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
              tooltip: {
          enabled: false,
          useHTML: true,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: 'var(--color-outline)',
          borderRadius: 6,
          padding: 10,
          shadow: true,
          style: {
            color: 'var(--color-text-primary)',
            fontSize: '13px'
          },
          formatter: function() {
            const nameParts = this.point.name.split(' -');
            const displayName = nameParts[0];
            const profit = nameParts.length > 1 ? nameParts[1] : '';
            
            let tooltipContent = `<div style="font-weight:bold; margin-bottom:5px;">${displayName}</div>`;
            
            if (profit) {
              const profitValue = parseFloat(profit);
              const profitColor = profitValue >= 0 ? '#28a745' : '#dc3545';
              const formattedProfit = profitValue.toFixed(2) + '%';
              tooltipContent += `<div>Rendimiento: <span style="color:${profitColor}; font-weight:bold;">${formattedProfit}</span></div>`;
            }
            
            tooltipContent += `<div>Tickers: ${this.point.value}</div>`;
            
            return tooltipContent;
          },
          animation: true
        },
      plotOptions: {
        series: {
          animation: {
            duration: 750
          },
          states: {
            hover: {
              animation: false
            }
          }
        }
      }
    },
    // Sector name translations
    sectorTranslations: {
      // Main sectors
      "Utilities": "Servicios Públicos",
      "Consumer Staples": "Productos Básicos de Consumo",
      "Financials": "Finanzas",
      "Materials": "Materiales",
      "Health Care": "Salud",
      "Industrials": "Industriales",
      "Real Estate": "Bienes Raíces",
      "Energy": "Energía",
      "Communication Services": "Servicios de Comunicación",
      "Consumer Discretionary": "Consumo Discrecional",
      "Information Technology": "Tecnología de la Información",
      
      // Sub-sectors
      "Consumer Staples Distribution & Retail": "Distribución y Venta de Productos Básicos",
      "Household & Personal Products": "Productos de Hogar y Personales",
      "Food, Beverage & Tobacco": "Alimentos, Bebidas y Tabaco",
      "Banks": "Bancos",
      "Diversified Financials": "Finanzas Diversificadas",
      "Insurance": "Seguros",
      "Financial Services": "Servicios Financieros",
      "Materials": "Materiales",
      "Pharmaceuticals, Biotechnology & Life Sciences": "Farmacéuticas, Biotecnología y Ciencias de la Vida",
      "Health Care Equipment & Services": "Equipos y Servicios de Salud",
      "Commercial & Professional Services": "Servicios Comerciales y Profesionales",
      "Transportation": "Transporte",
      "Capital Goods": "Bienes de Capital",
      "Equity Real Estate Investment Trusts (REITs)": "Fideicomisos de Inversión en Bienes Raíces (REITs)",
      "Real Estate": "Bienes Raíces",
      "Energy": "Energía",
      "Media & Entertainment": "Medios y Entretenimiento",
      "Telecommunication Services": "Servicios de Telecomunicaciones",
      "Consumer Durables & Apparel": "Bienes de Consumo Duraderos y Ropa",
      "Consumer Services": "Servicios al Consumidor",
      "Consumer Discretionary Distribution & Retail": "Distribución y Venta de Consumo Discrecional",
      "Semiconductors & Semiconductor Equipment": "Semiconductores y Equipos Semiconductores",
      "Technology Hardware & Equipment": "Hardware y Equipos Tecnológicos",
      "Software & Services": "Software y Servicios",
      "Utilities": "Servicios Públicos"
    },
    showInfoSection: false
  }),

  computed: {
    processedSectorData() {
      if (!this.sectors_data || 
          !this.sectors_data[this.activeButton]) {
        return [];
      }
      
      // Get the raw sector data (not treemap series)
      const sectorData = this.sectors_data[this.activeButton];
      // Filter out the chart_treemap_data_series key
      const sectors = Object.values(sectorData)
        .filter(item => typeof item === 'object' && !Array.isArray(item));
      
      // Map the treemap series for matching with subsectors
      const treemapSeries = sectorData.chart_treemap_data_series || [];
      
      // Create a map of sector IDs to their full data from the treemap series
      const sectorIdMap = new Map();
      treemapSeries.forEach(item => {
        if (!item.parent) {
          sectorIdMap.set(item.id, item);
        }
      });
      
      // Process each sector to include its subsectors
      return sectors.map(sector => {
        // Find the corresponding treemap data
        const treemapInfo = treemapSeries.find(item => 
          !item.parent && item.name.startsWith(sector.name)
        );
        
        // Get subsector data
        const specificSectors = [];
        if (sector.sub_sector) {
          Object.entries(sector.sub_sector).forEach(([name, data]) => {
            // Find matching treemap item for this subsector
            const treemapSubsector = treemapSeries.find(item => 
              item.parent && item.name === name
            );
            
            if (treemapSubsector) {
              specificSectors.push({
                id: `${sector.name}-${name}`,
                name: name,
                value: parseInt(data.eureker_tickers, 10),
                parentName: sector.name
              });
            }
          });
        }
        
        // Sort subsectors by value (eureker_tickers)
        specificSectors.sort((a, b) => b.value - a.value);
        
        // Return the structured data
        return {
          ...sector,
          id: treemapInfo?.id || sector.name,
          specificSectors
        };
      }).sort((a, b) => parseFloat(b.profit) - parseFloat(a.profit));
    },
    
    translatedTableData() {
      if (!this.sectors_data || !this.sectors_data.table_sector) {
        return [];
      }
      
      // Create a deep copy and translate sector names
      return this.sectors_data.table_sector.map(sector => {
        // Create a new object to avoid mutating the original data
        const translatedSector = { 
          ...sector,
          displayName: this.getTranslatedSectorName(sector.name),
          originalName: sector.name // Keep original name for lookups
        };
        
        // Keep the original 'name' if needed elsewhere, or decide if displayName replaces it for table
        // For now, displayName is for display, originalName for logic
        return translatedSector;
      });
    }
  },

  mounted() {
    this._initGraph();
  },
  
  watch: {
    sectors_data: {
      handler() {
        if (this.sectors_data && 
            this.sectors_data[this.activeButton] && 
            this.sectors_data[this.activeButton].chart_treemap_data_series) {
          this._initGraph();
        }
      },
      deep: true // Watch for nested changes in sectors_data
    }
  },
  
  methods: {
    _initGraph() {
      if (this.sectors_data && 
          this.sectors_data[this.activeButton] && 
          this.sectors_data[this.activeButton].chart_treemap_data_series) {
        // Get the treemap data
        const originalData = this.sectors_data[this.activeButton].chart_treemap_data_series;
        
        // Filter to only include main sectors (those without a parent)
        const mainSectorsOnly = originalData.filter(item => !item.parent);
        
        // Create a deep copy and translate the names for the chart
        const translatedData = mainSectorsOnly.map(item => {
          // Create a copy of the item to avoid mutating the original data
          const translatedItem = {...item};
          
          // Extract sector name and profit info using multiple patterns
          let sectorName = '';
          let profitInfo = '';
          
          // Try to match the "Name -X.XX%" pattern first
          const dashPattern = item.name.match(/^(.+?)\s+-(.+?)$/);
          if (dashPattern) {
            sectorName = dashPattern[1].trim();
            profitInfo = '-' + dashPattern[2].trim();
          } else {
            // Try to match the "Name X.XX%" pattern (space followed by number and %)
            const spacePercentPattern = item.name.match(/^(.+?)\s+(-?\d+\.?\d*%?)$/);
            if (spacePercentPattern) {
              sectorName = spacePercentPattern[1].trim();
              profitInfo = spacePercentPattern[2].trim();
              // Add % if it's just a number
              if (!profitInfo.includes('%')) {
                profitInfo += '%';
              }
            } else {
              // If no pattern matches, use the whole name
              sectorName = item.name.trim();
            }
          }
          
          // Get translated name
          const translatedName = this.getTranslatedSectorName(sectorName);
          
          // Set the translated name with profit info if available
          if (profitInfo) {
            // Format profit to 2 decimal places
            const profitValue = parseFloat(profitInfo.replace('%', ''));
            const formattedProfit = profitValue.toFixed(2) + '%';
            translatedItem.name = `${translatedName} ${formattedProfit}`;
          } else {
            translatedItem.name = translatedName;
          }
          
          // Calculate value based on all subsectors
          const subsectors = originalData.filter(subitem => 
            subitem.parent === item.id
          );
          
          if (subsectors.length > 0) {
            // Sum the values of all subsectors
            translatedItem.value = subsectors.reduce((sum, subsector) => 
              sum + (subsector.value || 0), 0
            );
          }
          
          return translatedItem;
        });
        
        // Update the chart with translated data
        this.treeOptions.series[0].data = translatedData;
      } else {
        this.treeOptions.series[0].data = [];
      }
    },
    
    setActiveButton(buttonId) {
      this.activeButton = buttonId;
      this._initGraph();
    },
    
    getTranslatedSectorName(name) {
      if (!name) return '';
      
      // First, clean the name by removing percentage patterns at the end
      // This regex looks for a space followed by a number (with optional decimal) and optional % sign at the end of the string
      const cleanName = name.replace(/\s+[-]?\d+\.?\d*%?$/, '').trim();
      
      // Also remove any profit information in the "Name -X.XX%" format
      const nameWithoutProfit = cleanName.split(' -')[0].trim();
      
      // Try to find a direct match in the translations
      if (this.sectorTranslations[nameWithoutProfit]) {
        return this.sectorTranslations[nameWithoutProfit];
      }
      
      // Handle special cases with ampersands and other formatting issues
      const normalizedName = nameWithoutProfit
        .replace(/\s+&amp;\s+/g, ' & ')  // Replace HTML-encoded ampersands
        .replace(/\s+and\s+/g, ' & ');   // Replace "and" with "&"
      
      if (this.sectorTranslations[normalizedName]) {
        return this.sectorTranslations[normalizedName];
      }
      
      return nameWithoutProfit;
    },
    
    formatSectorProfit(profit) {
      if (!profit) return "N/A";
      const numValue = parseFloat(profit);
      return numValue.toFixed(2) + "%";
    },
    
    getProfitClass(profit) {
      if (!profit) return "";
      
      const numValue = parseFloat(profit);
      if (numValue > 0) {
        return "profit-positive";
      } else if (numValue < 0) {
        return "profit-negative";
      }
      return "";
    },
    toggleInfoSection() {
      this.showInfoSection = !this.showInfoSection;
    },
  },
}
</script>


<style lang="scss" scoped>
  :root {
    --color-primary: #00add8;
    --color-background: #f5f6fa;
    --color-surface: #ffffff;
    --color-active-surface: #e8f5ff;
    --color-text-primary: #000010;
    --color-text-secondary: #5f6368;
    --color-muted: #CDD1DF;
    --border-radius: 20px;
    --transition-speed: 0.3s;
    --shadow-light: 0 1px 3px rgba(0, 0, 0, 0.1);
    --shadow-medium: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .sectores-container {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  .chart-index {
    position: relative;
    margin-bottom: 8px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    background-color: var(--color-surface);
    border: 1px solid var(--color-outline);
    transition: box-shadow 0.3s ease;
    
    &:hover {
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
    }
  }

  .chart-header {
    padding: 20px 24px;
    border-bottom: 1px solid var(--color-outline);
    background-color: var(--color-background);
    display: flex;
    justify-content: space-between;
    flex-direction: column;
  }

  .header-content {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
  }

  .chart-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: var(--color-text-primary);
    letter-spacing: -0.25px;
  }

  .info-toggle {
    background: none;
    border: none;
    padding: 8px;
    cursor: pointer;
    color: var(--color-text-secondary);
    border-radius: 50%;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background-color: var(--color-active-surface);
      color: var(--color-primary);
      transform: scale(1.1);
    }
    
    .material-icons {
      font-size: 20px;
    }
  }

  /* Info Section Styles */
  .info-section {
    margin-top: 20px;
    border-top: 1px solid var(--color-outline);
    padding-top: 20px;
  }

  .info-card {
    background: linear-gradient(135deg, var(--color-surface) 0%, rgba(255,255,255,0.8) 100%);
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    border: 1px solid var(--color-outline);
    transition: all 0.3s ease;
  }

  .info-card:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  .info-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }

  .info-header .material-icons {
    color: var(--color-primary);
    font-size: 20px;
  }

  .info-header h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .info-content p {
    margin: 0 0 16px 0;
    color: var(--color-text-secondary);
    line-height: 1.5;
    font-size: 14px;
  }

  .indicators-explanation {
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  .indicator-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .indicator-badge {
    padding: 4px 10px;
    border-radius: 16px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: white;
  }

  .indicator-badge.corto {
    background: linear-gradient(135deg, #ff6b6b, #ff5252);
  }

  .indicator-badge.medio {
    background: linear-gradient(135deg, #ffd93d, #ffb74d);
    color: var(--color-text-primary);
  }

  .indicator-badge.largo {
    background: linear-gradient(135deg, #6bcf7f, #4caf50);
  }

  .indicator-item span {
    color: var(--color-text-secondary);
    font-size: 12px;
  }

  .explanation-note {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background-color: rgba(0, 173, 217, 0.1);
    border-radius: 8px;
    border-left: 4px solid #00add9;
  }

  .explanation-note .material-icons {
    color: #00add9;
    font-size: 16px;
  }

  .explanation-note span:last-child {
    color: var(--color-text-primary);
    font-size: 12px;
    font-weight: 500;
  }

  .chart-content {
    padding: 24px;
    position: relative;
  }

  .visualization-wrapper {
    display: flex;
    flex-direction: row;
    gap: 24px;

    @media (max-width: 1200px) {
      flex-direction: column;
    }
  }

  .treemap-column {
    flex: 1.5; // Give treemap a bit more space initially
    display: flex;
    flex-direction: column;
    min-width: 0; // Prevents flex item from overflowing
  }

  .treemap-wrapper {
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid var(--color-outline);
    background-color: var(--color-background);
    min-height: 300px;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  .controls-container { // For treemap buttons
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 16px; // Placed below treemap in its column
    margin-bottom: 8px;
  }
  
  // Styles for time buttons (copied from original location, can be componentized later)
  .time-controls {
    display: flex;
    justify-content: center;
  }

  .time-buttons {
    display: inline-flex;
    background-color: var(--color-background);
    border-radius: 28px;
    padding: 4px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    border: 1px solid var(--color-outline);
    transition: box-shadow 0.3s ease;
    
    &:hover {
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
    }
  }

  .time-btn {
    background-color: transparent;
    color: var(--color-text-secondary);
    border: none;
    border-radius: 24px;
    padding: 10px 20px;
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0.25px;
    cursor: pointer;
    transition: all 0.3s ease;
    min-width: 100px;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 120%;
      height: 0;
      padding-bottom: 120%;
      background: radial-gradient(circle, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 70%);
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.8);
      transition: opacity 0.5s ease, transform 0.5s ease;
    }
    
    &:hover:not(.active) {
      background-color: var(--color-active-surface);
      color: var(--color-text-primary);
      transform: translateY(-1px);
      
      &::after {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1);
      }
    }
    
    &:active:not(.active) {
      transform: translateY(1px);
    }
    
    &.active {
      background-color: #00add9;
      color: white;
      font-weight: 600;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
      
      &:hover {
        background-color: #2980b9;
      }
    }
  }

  .btn-label {
    font-size: 14px;
    font-weight: 500;
    text-transform: capitalize;
  }

  .btn-subtitle {
    font-size: 11px;
    font-weight: 400;
    opacity: 0.8;
  }

  // New Nested Bars Column Styles
  .nested-bars-column {
    flex: 1;
    display: flex;
    flex-direction: column;
    border-left: 1px solid var(--color-outline);
    padding-left: 24px;
    min-width: 0; // Prevents flex item from overflowing
    max-height: calc(450px + 58px); // Match treemap wrapper height + approx controls height
    overflow-y: auto;

    @media (max-width: 1200px) {
      border-left: none;
      padding-left: 0;
      margin-top: 24px;
      border-top: 1px solid var(--color-outline);
      padding-top: 24px;
      max-height: 400px; // Specific height for mobile stacked view
    }
  }

  .no-data-message, .no-specific-sectors {
    color: var(--color-text-secondary);
    font-style: italic;
    padding: 16px;
    text-align: center;
  }

  .nested-bars-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .broad-sector-group {
    border-bottom: 1px solid var(--color-muted);
    padding-bottom: 16px;
    
    &:last-child {
      border-bottom: none;
    }
  }

  .broad-sector-header {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;
  }
  
  .sector-name-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .broad-sector-name {
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 0;
  }
  
  .sector-profit {
    font-size: 14px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 4px;
    
    &.profit-positive {
      color: var(--color-success, #28a745);
      background-color: rgba(40, 167, 69, 0.1);
    }
    
    &.profit-negative {
      color: var(--color-error, #dc3545);
      background-color: rgba(220, 53, 69, 0.1);
    }
  }

  .specific-sectors-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-left: 16px; // Indent specific sectors
  }

  .specific-sector-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 13px;
  }

  .specific-sector-name {
    color: var(--color-text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-grow: 1;
  }

  .specific-sector-tickers {
    color: var(--color-text-primary);
    font-weight: 500;
    padding-left: 8px;
  }

  .sectors-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    justify-content: space-between;
    
    @media (max-width: 839px) {
      flex-direction: column;
    }
  }

  /* Override Highcharts styles */
  :deep(.highcharts-container) {
    border-radius: 8px;
  }
  
  :deep(.highcharts-point) {
    transition: fill 0.3s ease, stroke-width 0.3s ease;
    
    &:hover {
      stroke-width: 2px;
      stroke: #fff;
    }
  }
</style>


